import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import type { CMSData, Property, Agent, NewsItem, ServiceItem, NavItem, Inquiry } from '../types/cms';
import cmsBackup from '../data/cms.json';

const API_BASE_URL = import.meta.env.VITE_CMS_API_URL || 'https://marveland-cms.jimsbond007.workers.dev';

// Pages Functions 邊緣注入的最新 CMS 資料（優先於靜態備份）
declare global {
  interface Window {
    __CMS_DATA__?: CMSData;
  }
}

const initialCMSData = (): CMSData => {
  if (typeof window !== 'undefined' && window.__CMS_DATA__) {
    return window.__CMS_DATA__;
  }
  return cmsBackup as CMSData;
};

interface CMSContextValue {
  cmsData: CMSData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateCMSData: (partial: Partial<CMSData>) => Promise<boolean>;
  submitInquiry: (inquiry: Partial<Inquiry>) => Promise<boolean>;
  getAgent: (agentId: string) => Agent | undefined;
  getProperty: (propertyId: string) => Property | undefined;
}

const CMSContext = createContext<CMSContextValue | null>(null);

export const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) throw new Error('useCMS must be used within CMSProvider');
  return context;
};

export const CMSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cmsData, setCmsData] = useState<CMSData>(initialCMSData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!API_BASE_URL) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/cms/data`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCmsData(data);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const updateCMSData = async (partial: Partial<CMSData>): Promise<boolean> => {
    if (!API_BASE_URL) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/api/cms/data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partial),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
      return true;
    } catch (err) {
      setError(String(err));
      return false;
    }
  };

  const submitInquiry = async (inquiry: Partial<Inquiry>): Promise<boolean> => {
    const endpoint = API_BASE_URL ? `${API_BASE_URL}/api/inquiries` : '/api/inquiries';
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inquiry),
      });
      return res.ok;
    } catch (err) {
      console.error('submitInquiry error:', err);
      return false;
    }
  };

  const getAgent = (agentId: string) => cmsData.agents.find((a) => a.id === agentId);
  const getProperty = (propertyId: string) => cmsData.properties.find((p) => p.id === propertyId);

  const value = useMemo(
    () => ({
      cmsData,
      loading,
      error,
      refresh,
      updateCMSData,
      submitInquiry,
      getAgent,
      getProperty,
    }),
    [cmsData, loading, error]
  );

  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
};

// Safe fallback helpers
export const useSiteConfig = () => useCMS().cmsData.siteConfig;
export const useNav = () => useCMS().cmsData.nav;
export const useHero = () => useCMS().cmsData.hero;
export const useAbout = () => useCMS().cmsData.about;
export const useServices = () => useCMS().cmsData.services;
export const useProperties = () => useCMS().cmsData.properties;
export const useAgents = () => useCMS().cmsData.agents;
export const useNews = () => useCMS().cmsData.news;
export const useContact = () => useCMS().cmsData.contact;
export const useOwnerForm = () => useCMS().cmsData.ownerForm;
export const useFooter = () => useCMS().cmsData.footer;
